/**
 * CrewManagementModal Component
 * 
 * Modal for managing crews in Actual Spent Hours.
 * Features:
 * - Create, edit, delete crews
 * - Assign crew head (employee)
 * - Add/remove crew members
 * - Crew Log showing all crews and their members
 * - Employee autocomplete for selection
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useMsal } from '@azure/msal-react';
import { authenticatedApiCall } from '../../utils/apiClient';
import { required, minArrayLength, validateSchema } from '../../utils/validation';
import EmployeeAutocomplete from '../common/EmployeeAutocomplete';
import GroupedProjectSelect from '../common/GroupedProjectSelect';
import { useToast } from '../../hooks/useToast';
import TimeTrackerHelpModal from './TimeTrackerHelpModal';
import { buildProjectDisciplineOptions } from './projectDisciplineOptions';

const CrewManagementModal = ({ projectId, onClose }) => {
  const { instance, accounts } = useMsal();
  const [crews, setCrews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { toast, showToast } = useToast();
  
  // Tracks whether Save was attempted (enables inline error messages)
  const [saveAttempted, setSaveAttempted] = useState(false);

  // View mode: 'list' shows crew log, 'form' shows add/edit form
  const [viewMode, setViewMode] = useState('list');
  const [showHelp, setShowHelp] = useState(false);
  
  // Projects list for dropdown
  const [projects, setProjects] = useState([]);
  
  // Fab Shop Foreman flag for multi-project support
  const [isFabShopForeman, setIsFabShopForeman] = useState(false);
  
  // Form state for new/edit crew
  const [editingCrew, setEditingCrew] = useState(null);
  const [formData, setFormData] = useState({
    crew_id: '',  // Auto-populated from crew head's name
    head: null,
    members: [],
    projects: []  // Array of { project_id, discipline_code, job_number }
  });
  const [memberToAdd, setMemberToAdd] = useState(null);
  const [fabShopProjectToAdd, setFabShopProjectToAdd] = useState(null); // optionKey for fab shop "add project" dropdown
  const [selectedRootProject, setSelectedRootProject] = useState(null); // { project_id, job_number } for regular crew root-project mode
  
  // Member transfer state
  const [memberTransfer, setMemberTransfer] = useState({
    empCode: null,         // Which member is being transferred
    targetCrewId: null,    // Target crew database ID
    showConfirm: false,    // Show confirmation dialog
    transferring: false    // Transfer in progress
  });
  const [projectCrews, setProjectCrews] = useState([]); // Other crews in same project for transfer
  
  // Expanded crews in the log view
  const [expandedCrews, setExpandedCrews] = useState(new Set());

  // Fetch crews on mount
  const fetchCrews = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/crews?projectId=${projectId}&includeMembers=true`
      );

      if (response.data.success) {
        setCrews(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching crews:', error);
      showToast('Failed to load crews', 'error');
    } finally {
      setLoading(false);
    }
  }, [instance, accounts, projectId, showToast]);

  // Fetch projects for dropdown
  const fetchProjects = useCallback(async () => {
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        '/projects'
      );
      
      if (response.data.projects) {
        // Sort projects by job number
        const sortedProjects = [...response.data.projects].sort((a, b) => {
          const numA = parseInt(a.job_number, 10);
          const numB = parseInt(b.job_number, 10);
          if (!isNaN(numA) && !isNaN(numB)) {
            return numA - numB;
          }
          return (a.job_number || '').localeCompare(b.job_number || '');
        });
        setProjects(sortedProjects);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  }, [instance, accounts]);

  useEffect(() => {
    fetchCrews();
    fetchProjects();
  }, [fetchCrews, fetchProjects]);

  // Transform projects into project-discipline combos for dropdown
  const projectDisciplineOptions = useMemo(
    () => buildProjectDisciplineOptions(projects),
    [projects]
  );

  // Fab Shop Foreman: filter to PFS-only options
  const fabShopOptions = useMemo(() => {
    return projectDisciplineOptions.filter(opt => opt.discipline_code === 'PFS');
  }, [projectDisciplineOptions]);

  // Regular crew: one entry per unique job_number for root-project selection
  const rootProjectOptions = useMemo(() => {
    const seen = new Set();
    return projects
      .filter(p => {
        if (seen.has(p.job_number)) return false;
        seen.add(p.job_number);
        return true;
      })
      .map(p => ({ project_id: p.id, job_number: p.job_number, job_name: p.job_name }))
      .sort((a, b) => a.job_number.localeCompare(b.job_number, undefined, { numeric: true }));
  }, [projects]);

  // Check if selected crew head is a Fab Shop Foreman
  const checkHeadRole = useCallback(async (empCode) => {
    if (!empCode) {
      setIsFabShopForeman(false);
      return;
    }
    try {
      const response = await authenticatedApiCall(
        instance, accounts, 'GET',
        `/crews/check-role?empCode=${encodeURIComponent(empCode)}`
      );
      if (response.data.success) {
        setIsFabShopForeman(response.data.isFabShopForeman);
      }
    } catch (error) {
      console.error('Error checking crew head role:', error);
      setIsFabShopForeman(false);
    }
  }, [instance, accounts]);

  // Toggle crew expansion in log view
  const toggleCrewExpansion = (crewId) => {
    setExpandedCrews(prev => {
      const newSet = new Set(prev);
      if (newSet.has(crewId)) {
        newSet.delete(crewId);
      } else {
        newSet.add(crewId);
      }
      return newSet;
    });
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      crew_id: '',
      head: null,
      members: [],
      projects: []
    });
    setMemberToAdd(null);
    setEditingCrew(null);
    setIsFabShopForeman(false);
    setSelectedRootProject(null);
    setSaveAttempted(false);
    setViewMode('list');
  };

  // Start editing a crew
  const handleEditCrew = (crew) => {
    setEditingCrew(crew);
    const crewProjects = (crew.projects || []).map(p => ({
      project_id: p.project_id,
      discipline_code: p.discipline_code,
      job_number: p.job_number
    }));
    setFormData({
      crew_id: crew.crew_id,
      head: crew.head_emp_code ? {
        emp_code: crew.head_emp_code,
        emp_name: crew.head_emp_name
      } : null,
      members: crew.members || [],
      projects: crewProjects
    });
    // Restore selectedRootProject if crew has projects on the same root job
    if (crewProjects.length > 0) {
      const uniqueProjectIds = [...new Set(crewProjects.map(p => p.project_id))];
      if (uniqueProjectIds.length === 1) {
        setSelectedRootProject({ project_id: crewProjects[0].project_id, job_number: crewProjects[0].job_number });
      } else {
        setSelectedRootProject(null);
      }
    } else {
      setSelectedRootProject(null);
    }
    // Check role for the crew head
    if (crew.head_emp_code) {
      checkHeadRole(crew.head_emp_code);
    }
    setViewMode('form');
  };

  // Start creating a new crew
  const handleNewCrew = () => {
    setFormData({
      crew_id: '',
      head: null,
      members: [],
      projects: []
    });
    setMemberToAdd(null);
    setEditingCrew(null);
    setIsFabShopForeman(false);
    setSelectedRootProject(null);
    setViewMode('form');
  };

  // Add member to form
  const handleAddMember = () => {
    if (!memberToAdd) return;
    
    // Check if already in list
    if (formData.members.some(m => m.emp_code === memberToAdd.emp_code)) {
      showToast('Employee already in crew', 'error');
      return;
    }
    
    setFormData(prev => ({
      ...prev,
      members: [...prev.members, {
        emp_code: memberToAdd.emp_code,
        emp_name: memberToAdd.emp_name,
        trade: memberToAdd.trade,
        division: memberToAdd.division
      }]
    }));
    setMemberToAdd(null);
  };

  // Remove member from form
  const handleRemoveMember = (empCode) => {
    setFormData(prev => ({
      ...prev,
      members: prev.members.filter(m => m.emp_code !== empCode)
    }));
  };

  // Fetch other crews in the same project for member transfer
  const fetchProjectCrews = useCallback(async () => {
    const firstProject = formData.projects?.[0];
    if (!firstProject?.project_id) {
      setProjectCrews([]);
      return;
    }
    
    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'GET',
        `/crews?projectId=${firstProject.project_id}`
      );
      
      if (response.data.success) {
        // Filter out the current crew being edited
        const otherCrews = (response.data.data || []).filter(
          crew => crew.id !== editingCrew?.id
        );
        setProjectCrews(otherCrews);
      }
    } catch (error) {
      console.error('Error fetching project crews:', error);
      setProjectCrews([]);
    }
  }, [instance, accounts, formData.projects, editingCrew]);

  // Fetch project crews when editing a crew
  useEffect(() => {
    if (editingCrew && formData.projects?.length > 0) {
      fetchProjectCrews();
    }
  }, [editingCrew, formData.projects, fetchProjectCrews]);

  // Handle member transfer to another crew
  const handleMemberTransfer = async () => {
    if (!memberTransfer.empCode || !memberTransfer.targetCrewId) return;
    
    const member = formData.members.find(m => m.emp_code === memberTransfer.empCode);
    if (!member) return;
    
    setMemberTransfer(prev => ({ ...prev, transferring: true }));
    
    try {
      // Add member to target crew
      const targetCrew = projectCrews.find(c => c.id === memberTransfer.targetCrewId);
      const targetMembers = [...(targetCrew?.members || []), {
        emp_code: member.emp_code,
        emp_name: member.emp_name,
        trade: member.trade,
        division: member.division
      }];
      
      await authenticatedApiCall(
        instance,
        accounts,
        'PUT',
        `/crews/${memberTransfer.targetCrewId}/members`,
        { members: targetMembers }
      );
      
      // Remove member from current crew's form data
      setFormData(prev => ({
        ...prev,
        members: prev.members.filter(m => m.emp_code !== memberTransfer.empCode)
      }));
      
      showToast(`${member.emp_name} transferred to ${targetCrew?.crew_id || 'new crew'}`, 'success');
      
      // Reset transfer state
      setMemberTransfer({
        empCode: null,
        targetCrewId: null,
        showConfirm: false,
        transferring: false
      });
      
      // Refresh project crews
      fetchProjectCrews();
    } catch (error) {
      console.error('Error transferring member:', error);
      showToast('Failed to transfer member', 'error');
      setMemberTransfer(prev => ({ ...prev, transferring: false }));
    }
  };

  // Reset member transfer state
  const resetMemberTransfer = () => {
    setMemberTransfer({
      empCode: null,
      targetCrewId: null,
      showConfirm: false,
      transferring: false
    });
  };

  // Form-level validation errors (active after first save attempt)
  const formErrors = useMemo(() => saveAttempted ? validateSchema(
    { head: formData.head, projects: formData.projects },
    {
      head:     [required('Crew head is required')],
      projects: [minArrayLength(1, 'At least one project must be assigned')],
    }
  ) : {}, [saveAttempted, formData.head, formData.projects]);

  // Save crew (create or update)
  const handleSaveCrew = async () => {
    setSaveAttempted(true);
    if (!formData.head) {
      showToast('Crew Head is required', 'error');
      return;
    }
    if (!formData.crew_id.trim()) {
      showToast('Crew ID is required (select a Crew Head)', 'error');
      return;
    }
    if (formData.projects.length === 0) {
      showToast('At least one project must be assigned to the crew', 'error');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        crew_id: formData.crew_id.trim(),
        crew_name: null,
        head_emp_code: formData.head?.emp_code || null,
        head_emp_name: formData.head?.emp_name || null,
        members: formData.members,
        projects: formData.projects
      };

      let response;
      if (editingCrew) {
        // Update existing crew
        response = await authenticatedApiCall(
          instance,
          accounts,
          'PUT',
          `/crews/${editingCrew.id}`,
          payload
        );
        
        // Also update members
        await authenticatedApiCall(
          instance,
          accounts,
          'PUT',
          `/crews/${editingCrew.id}/members`,
          { members: formData.members }
        );
      } else {
        // Create new crew
        response = await authenticatedApiCall(
          instance,
          accounts,
          'POST',
          '/crews',
          payload
        );
      }

      if (response.data.success) {
        showToast(editingCrew ? 'Crew updated successfully' : 'Crew created successfully', 'success');
        setViewMode('list');
        setEditingCrew(null);
        setFormData({ crew_id: '', head: null, members: [], projects: [] });
        setIsFabShopForeman(false);
        setSelectedRootProject(null);
        fetchCrews();
      } else {
        throw new Error(response.data.message || 'Failed to save crew');
      }
    } catch (error) {
      console.error('Error saving crew:', error);
      showToast(error.response?.data?.message || error.message || 'Failed to save crew', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Delete crew
  const handleDeleteCrew = async (crewId) => {
    if (!window.confirm('Are you sure you want to delete this crew? This action cannot be undone.')) {
      return;
    }

    try {
      const response = await authenticatedApiCall(
        instance,
        accounts,
        'DELETE',
        `/crews/${crewId}`
      );

      if (response.data.success) {
        showToast('Crew deleted successfully', 'success');
        fetchCrews();
        if (editingCrew?.id === crewId) {
          resetForm();
        }
      }
    } catch (error) {
      console.error('Error deleting crew:', error);
      showToast('Failed to delete crew', 'error');
    }
  };

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000
    }}>
      {/* Toast */}
      {toast.show && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          padding: '1rem 1.5rem',
          borderRadius: '8px',
          backgroundColor: toast.type === 'success' ? '#28a745' : '#dc3545',
          color: 'white',
          zIndex: 1100,
          boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
        }}>
          {toast.message}
        </div>
      )}

      <div style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        width: '95%',
        maxWidth: '1000px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 8px 32px rgba(0,0,0,0.2)'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.5rem',
          borderBottom: '1px solid #dee2e6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          backgroundColor: '#f8f9fa'
        }}>
          <div style={{ minWidth: 0 }}>
            <h2 style={{ margin: 0, fontSize: '1.25rem', color: '#333' }}>
              Crews
            </h2>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, maxWidth: 520, lineHeight: 1.4 }}>
              You can create and change the crew you head, and transfer it to someone else. Accounting and superadmin can change any crew. Every change is written to Audit.
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button
            type="button"
            onClick={() => setShowHelp(true)}
            style={{
              padding: '8px 12px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#0d9488',
              color: 'white',
              cursor: 'pointer',
              fontSize: '0.8rem',
              fontWeight: 600,
            }}
          >
            ❓ Help
          </button>
          <button
            onClick={onClose}
            style={{
              padding: '8px 16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: '#6c757d',
              color: 'white',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ flex: 1, overflow: 'auto', display: 'flex', flexDirection: 'column' }}>
          
          {/* View Toggle & Actions */}
          <div style={{ 
            padding: '1rem 1.5rem', 
            borderBottom: '1px solid #dee2e6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            backgroundColor: '#f8f9fa'
          }}>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                onClick={() => setViewMode('list')}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: viewMode === 'list' ? '#007bff' : '#e9ecef',
                  color: viewMode === 'list' ? 'white' : '#333',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                📋 Crew Log
              </button>
              <button
                onClick={handleNewCrew}
                style={{
                  padding: '8px 16px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: viewMode === 'form' && !editingCrew ? '#007bff' : '#28a745',
                  color: 'white',
                  cursor: 'pointer',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}
              >
                + New Crew
              </button>
            </div>
            <div style={{ fontSize: '0.9rem', color: '#6c757d' }}>
              {crews.length} crew{crews.length !== 1 ? 's' : ''} | {crews.reduce((sum, c) => sum + (c.members?.length || 0), 0)} total members
            </div>
          </div>

          {/* Main Content Area */}
          <div style={{ flex: 1, overflow: 'auto' }}>
            
            {/* CREW LOG VIEW */}
            {viewMode === 'list' && (
              <div style={{ padding: '1rem' }}>
                {loading ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                    Loading crews...
                  </div>
                ) : crews.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                    <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>👥</div>
                    <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>No crews created yet</div>
                    <div style={{ fontSize: '0.9rem' }}>Click "New Crew" to create your first crew</div>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {crews.map(crew => (
                      <div
                        key={crew.id}
                        style={{
                          backgroundColor: 'white',
                          border: '1px solid #dee2e6',
                          borderRadius: '8px',
                          overflow: 'hidden',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                        }}
                      >
                        {/* Crew Header */}
                        <div
                          onClick={() => toggleCrewExpansion(crew.id)}
                          style={{
                            padding: '1rem',
                            backgroundColor: '#f8f9fa',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: expandedCrews.has(crew.id) ? '1px solid #dee2e6' : 'none'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <span style={{ fontSize: '1.2rem' }}>
                              {expandedCrews.has(crew.id) ? '▼' : '▶'}
                            </span>
                            <div>
                              <div style={{ fontWeight: '600', fontSize: '1.1rem', color: '#333' }}>
                                {crew.crew_id}
                                {crew.crew_name && (
                                  <span style={{ fontWeight: '400', color: '#6c757d', marginLeft: '8px' }}>
                                    - {crew.crew_name}
                                  </span>
                                )}
                              </div>
                              {crew.head_emp_name && (
                                <div style={{ fontSize: '0.85rem', color: '#495057', marginTop: '4px' }}>
                                  <span style={{ color: '#ffc107' }}>★</span> Head: <strong>{crew.head_emp_name}</strong>
                                  <span style={{ color: '#6c757d' }}> ({crew.head_emp_code})</span>
                                </div>
                              )}
                              {/* Display assigned project-discipline combos */}
                              <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '2px' }}>
                                Project: {crew.projects && crew.projects.length > 0 ? (
                                  <span style={{ color: '#17a2b8' }}>
                                    <strong>
                                      {crew.projects.map(p => p.display_value || `${p.job_number}${p.discipline_code}`).join(', ')}
                                    </strong>
                                  </span>
                                ) : crew.job_number ? (
                                  <span style={{ color: '#17a2b8' }}>
                                    <strong>{crew.job_number}</strong> - {crew.job_name || 'Unnamed'}
                                  </span>
                                ) : (
                                  <span style={{ fontStyle: 'italic' }}>No project assigned</span>
                                )}
                              </div>
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                            <span style={{
                              backgroundColor: '#007bff',
                              color: 'white',
                              padding: '4px 12px',
                              borderRadius: '12px',
                              fontSize: '0.8rem',
                              fontWeight: '500'
                            }}>
                              {crew.members?.length || 0} member{(crew.members?.length || 0) !== 1 ? 's' : ''}
                            </span>
                            <button
                              onClick={(e) => { e.stopPropagation(); handleEditCrew(crew); }}
                              style={{
                                padding: '6px 12px',
                                border: '1px solid #007bff',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                color: '#007bff',
                                cursor: 'pointer',
                                fontSize: '0.8rem'
                              }}
                            >
                              Edit
                            </button>
                          </div>
                        </div>
                        
                        {/* Crew Members (Expanded) */}
                        {expandedCrews.has(crew.id) && (
                          <div style={{ padding: '1rem' }}>
                            {(!crew.members || crew.members.length === 0) ? (
                              <div style={{ textAlign: 'center', padding: '1rem', color: '#6c757d', fontStyle: 'italic' }}>
                                No members in this crew
                              </div>
                            ) : (
                              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
                                <thead>
                                  <tr style={{ backgroundColor: '#e9ecef' }}>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600' }}>Employee Name</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600' }}>Employee Code</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600' }}>Trade</th>
                                    <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: '600' }}>Division</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {crew.members.map((member, idx) => (
                                    <tr key={member.emp_code} style={{ backgroundColor: idx % 2 === 0 ? 'white' : '#f8f9fa' }}>
                                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{member.emp_name}</td>
                                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee', fontFamily: 'monospace' }}>{member.emp_code}</td>
                                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{member.trade || '-'}</td>
                                      <td style={{ padding: '8px 12px', borderBottom: '1px solid #eee' }}>{member.division || '-'}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* FORM VIEW (Add/Edit Crew) */}
            {viewMode === 'form' && (
              <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                {/* Form Header */}
                <div style={{ 
                  padding: '1rem', 
                  borderBottom: '1px solid #dee2e6',
                  backgroundColor: '#f8f9fa'
                }}>
                  <h3 style={{ margin: 0, fontSize: '1rem' }}>
                    {editingCrew ? `Edit: ${editingCrew.crew_id}` : 'New Crew'}
                  </h3>
                </div>

                {/* Form Content */}
                <div style={{ flex: 1, overflow: 'auto', padding: '1.5rem' }}>
                  {/* Crew Head Selection (Primary - determines Crew ID) */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '0.9rem' }}>
                      <span style={{ color: '#ffc107' }}>★</span> Crew Head <span style={{ color: '#dc3545' }}>*</span>
                    </label>
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ border: formErrors.head ? '1px solid #dc3545' : 'none', borderRadius: '4px' }}>
                          <EmployeeAutocomplete
                            value={formData.head}
                            onChange={(emp) => {
                              setFormData(prev => ({ 
                                ...prev, 
                                head: emp,
                                crew_id: emp ? emp.emp_name : '',
                                // Clear projects when head changes (role may differ)
                                projects: []
                              }));
                              // Check if new head is a Fab Shop Foreman
                              checkHeadRole(emp?.emp_code);
                            }}
                            projectId={projectId}
                            placeholder="Search for crew head..."
                          />
                        </div>
                        {formErrors.head && (
                          <div style={{ color: '#dc3545', fontSize: '0.78rem', marginTop: '3px' }}>{formErrors.head}</div>
                        )}
                      </div>
                      {formData.head && (
                        <div style={{ 
                          padding: '8px 12px', 
                          backgroundColor: '#fff3cd', 
                          borderRadius: '4px',
                          fontSize: '0.85rem'
                        }}>
                          <strong>{formData.head.emp_code}</strong>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Crew ID (Auto-populated from Crew Head name, read-only) */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '0.9rem' }}>
                      Crew ID
                    </label>
                    <input
                      type="text"
                      value={formData.crew_id}
                      readOnly
                      placeholder="Auto-populated from Crew Head"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        fontSize: '0.9rem',
                        boxSizing: 'border-box',
                        backgroundColor: '#e9ecef',
                        color: '#495057',
                        cursor: 'not-allowed'
                      }}
                    />
                    <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '4px' }}>
                      Automatically set to the Crew Head's name
                    </div>
                  </div>

                  {/* Associated Project-Discipline */}
                  <div style={{ marginBottom: '1.5rem' }}>
                    <label style={{ display: 'block', marginBottom: '4px', fontWeight: '500', fontSize: '0.9rem' }}>
                      Associated Project{isFabShopForeman ? 's (Fab Shop - PFS only)' : ''}
                    </label>
                    
                    {isFabShopForeman ? (
                      /* Fab Shop Foreman: multi-project assignment (PFS only) */
                      <div>
                        {/* Assigned projects tags */}
                        {formData.projects.length > 0 && (
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '8px' }}>
                            {formData.projects.map((proj, idx) => (
                              <span key={idx} style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '4px 10px',
                                backgroundColor: '#e8f5e9',
                                border: '1px solid #a5d6a7',
                                borderRadius: '16px',
                                fontSize: '0.85rem',
                                fontWeight: '500',
                                color: '#2e7d32'
                              }}>
                                {proj.job_number}{proj.discipline_code}
                                <button
                                  onClick={() => setFormData(prev => ({
                                    ...prev,
                                    projects: prev.projects.filter((_, i) => i !== idx)
                                  }))}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    fontSize: '1rem',
                                    color: '#c62828',
                                    padding: '0',
                                    lineHeight: 1
                                  }}
                                  title="Remove project"
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                        )}
                        {/* Add project dropdown */}
                        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                          <div style={{ flex: 1 }}>
                            <GroupedProjectSelect
                              value={fabShopProjectToAdd}
                              onChange={(optionKey) => setFabShopProjectToAdd(optionKey)}
                              options={fabShopOptions.filter(opt => !formData.projects.some(p => p.project_id === opt.project_id && p.discipline_code === opt.discipline_code))}
                              placeholder="Select PFS project to add..."
                            />
                          </div>
                          <button
                            onClick={() => {
                              if (!fabShopProjectToAdd) return;
                              const selected = fabShopOptions.find(opt => opt.optionKey === fabShopProjectToAdd);
                              if (selected) {
                                setFormData(prev => ({
                                  ...prev,
                                  projects: [...prev.projects, {
                                    project_id: selected.project_id,
                                    discipline_code: selected.discipline_code,
                                    job_number: selected.job_number
                                  }]
                                }));
                                setFabShopProjectToAdd(null);
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              border: 'none',
                              borderRadius: '4px',
                              backgroundColor: '#28a745',
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '0.9rem',
                              fontWeight: '500',
                              marginTop: '1px'
                            }}
                          >
                            Add
                          </button>
                        </div>
                        <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '4px' }}>
                          Fab Shop Foreman can assign multiple PFS projects to their crew
                        </div>
                      </div>
                    ) : (
                      /* Regular crew: root-project selection — all disciplines auto-expanded */
                      <div>
                        {selectedRootProject ? (
                          /* Root project chosen — show discipline tags + clear option */
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                              <span style={{
                                padding: '5px 12px',
                                backgroundColor: '#e3f2fd',
                                border: '1px solid #90caf9',
                                borderRadius: '4px',
                                fontSize: '0.9rem',
                                fontWeight: '600',
                                color: '#1565c0'
                              }}>
                                {selectedRootProject.job_number}
                              </span>
                              <button
                                type="button"
                                onClick={() => {
                                  setSelectedRootProject(null);
                                  setFormData(prev => ({ ...prev, projects: [] }));
                                }}
                                style={{
                                  background: 'none',
                                  border: '1px solid #fca5a5',
                                  borderRadius: '4px',
                                  color: '#dc2626',
                                  cursor: 'pointer',
                                  fontSize: '0.8rem',
                                  padding: '3px 8px'
                                }}
                              >
                                Change
                              </button>
                            </div>
                            {formData.projects.length > 0 && (
                              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                                {formData.projects.map((proj, idx) => (
                                  <span key={idx} style={{
                                    padding: '3px 10px',
                                    backgroundColor: '#f0fdf4',
                                    border: '1px solid #a5d6a7',
                                    borderRadius: '12px',
                                    fontSize: '0.8rem',
                                    fontWeight: '500',
                                    color: '#2e7d32'
                                  }}>
                                    {proj.job_number}{proj.discipline_code}
                                  </span>
                                ))}
                              </div>
                            )}
                            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '6px' }}>
                              All disciplines for this project will be available in the timesheet
                            </div>
                          </div>
                        ) : (
                          /* No root project selected yet — show GroupedProjectSelect */
                          <div>
                            <GroupedProjectSelect
                              value={null}
                              options={projectDisciplineOptions}
                              placeholder="Select project or discipline..."
                              onChange={(optionKey) => {
                                // User picked a specific discipline → single-discipline assignment
                                const selected = projectDisciplineOptions.find(o => o.optionKey === optionKey);
                                if (!selected) return;
                                setSelectedRootProject({ project_id: selected.project_id, job_number: selected.job_number });
                                setFormData(prev => ({
                                  ...prev,
                                  projects: [{
                                    project_id: selected.project_id,
                                    discipline_code: selected.discipline_code,
                                    job_number: selected.job_number
                                  }]
                                }));
                              }}
                              onGroupSelect={(jobNumber) => {
                                // User clicked the project header → assign ALL disciplines for that project
                                const rootProj = rootProjectOptions.find(p => p.job_number === jobNumber);
                                if (!rootProj) return;
                                const disciplines = projectDisciplineOptions.filter(
                                  opt => opt.project_id === rootProj.project_id
                                );
                                setSelectedRootProject({ project_id: rootProj.project_id, job_number: rootProj.job_number });
                                setFormData(prev => ({
                                  ...prev,
                                  projects: disciplines.map(d => ({
                                    project_id: d.project_id,
                                    discipline_code: d.discipline_code,
                                    job_number: d.job_number
                                  }))
                                }));
                              }}
                              style={{ width: '100%' }}
                            />
                            <div style={{ fontSize: '0.8rem', color: '#6c757d', marginTop: '4px' }}>
                              Click a <strong>discipline</strong> (e.g. 99999CIV) to assign it, or click the <strong>project number</strong> to assign all disciplines
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Crew Members */}
                  <div>
                    <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', fontSize: '0.9rem' }}>
                      Crew Members ({formData.members.length})
                    </label>
                    
                    {/* Add Member */}
                    <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
                      <div style={{ flex: 1 }}>
                        <EmployeeAutocomplete
                          value={memberToAdd}
                          onChange={setMemberToAdd}
                          projectId={projectId}
                          placeholder="Search to add member..."
                        />
                      </div>
                      <button
                        onClick={handleAddMember}
                        disabled={!memberToAdd}
                        style={{
                          padding: '8px 16px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: memberToAdd ? '#28a745' : '#dee2e6',
                          color: memberToAdd ? 'white' : '#6c757d',
                          cursor: memberToAdd ? 'pointer' : 'not-allowed',
                          fontSize: '0.9rem'
                        }}
                      >
                        Add
                      </button>
                    </div>

                    {/* Members List */}
                    <div style={{ 
                      border: '1px solid #dee2e6', 
                      borderRadius: '4px',
                      maxHeight: '350px',
                      overflow: 'auto'
                    }}>
                      {formData.members.length === 0 ? (
                        <div style={{ padding: '1rem', textAlign: 'center', color: '#6c757d' }}>
                          No members added yet
                        </div>
                      ) : (
                        formData.members.map((member, index) => (
                          <div
                            key={member.emp_code}
                            style={{
                              padding: '10px 12px',
                              borderBottom: index < formData.members.length - 1 ? '1px solid #f0f0f0' : 'none',
                              backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                            }}
                          >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <div>
                                <div style={{ fontWeight: '500' }}>{member.emp_name}</div>
                                <div style={{ fontSize: '0.8rem', color: '#6c757d' }}>
                                  {member.emp_code}
                                  {member.trade && ` • ${member.trade}`}
                                  {member.division && ` • ${member.division}`}
                                </div>
                              </div>
                              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                {/* Transfer controls - only show when editing existing crew */}
                                {editingCrew && projectCrews.length > 0 && (
                                  <>
                                    <select
                                      value={memberTransfer.empCode === member.emp_code ? (memberTransfer.targetCrewId || '') : ''}
                                      onChange={(e) => setMemberTransfer({
                                        empCode: member.emp_code,
                                        targetCrewId: e.target.value ? parseInt(e.target.value, 10) : null,
                                        showConfirm: false,
                                        transferring: false
                                      })}
                                      style={{
                                        padding: '4px 8px',
                                        border: '1px solid #dee2e6',
                                        borderRadius: '4px',
                                        fontSize: '0.75rem',
                                        minWidth: '120px'
                                      }}
                                    >
                                      <option value="">Transfer to...</option>
                                      {projectCrews.map(crew => (
                                        <option key={crew.id} value={crew.id}>
                                          {crew.crew_id}
                                        </option>
                                      ))}
                                    </select>
                                    <button
                                      onClick={() => {
                                        if (memberTransfer.empCode === member.emp_code && memberTransfer.targetCrewId) {
                                          setMemberTransfer(prev => ({ ...prev, showConfirm: true }));
                                        }
                                      }}
                                      disabled={memberTransfer.empCode !== member.emp_code || !memberTransfer.targetCrewId}
                                      style={{
                                        padding: '4px 8px',
                                        border: 'none',
                                        borderRadius: '4px',
                                        backgroundColor: (memberTransfer.empCode === member.emp_code && memberTransfer.targetCrewId) ? '#ffc107' : '#e9ecef',
                                        color: (memberTransfer.empCode === member.emp_code && memberTransfer.targetCrewId) ? '#333' : '#6c757d',
                                        cursor: (memberTransfer.empCode === member.emp_code && memberTransfer.targetCrewId) ? 'pointer' : 'not-allowed',
                                        fontSize: '0.75rem'
                                      }}
                                    >
                                      Transfer
                                    </button>
                                  </>
                                )}
                                <button
                                  onClick={() => handleRemoveMember(member.emp_code)}
                                  style={{
                                    padding: '4px 8px',
                                    border: 'none',
                                    borderRadius: '4px',
                                    backgroundColor: '#dc3545',
                                    color: 'white',
                                    cursor: 'pointer',
                                    fontSize: '0.75rem'
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            </div>
                            
                            {/* Transfer Confirmation */}
                            {memberTransfer.empCode === member.emp_code && memberTransfer.showConfirm && (
                              <div style={{
                                marginTop: '8px',
                                padding: '8px',
                                backgroundColor: '#fff3cd',
                                borderRadius: '4px',
                                border: '1px solid #ffc107'
                              }}>
                                <div style={{ fontSize: '0.8rem', marginBottom: '8px', color: '#856404' }}>
                                  Transfer <strong>{member.emp_name}</strong> to crew{' '}
                                  <strong>{projectCrews.find(c => c.id === memberTransfer.targetCrewId)?.crew_id}</strong>?
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem' }}>
                                  <button
                                    onClick={handleMemberTransfer}
                                    disabled={memberTransfer.transferring}
                                    style={{
                                      padding: '4px 12px',
                                      border: 'none',
                                      borderRadius: '4px',
                                      backgroundColor: memberTransfer.transferring ? '#6c757d' : '#28a745',
                                      color: 'white',
                                      cursor: memberTransfer.transferring ? 'not-allowed' : 'pointer',
                                      fontSize: '0.75rem',
                                      fontWeight: '500'
                                    }}
                                  >
                                    {memberTransfer.transferring ? 'Transferring...' : 'Confirm'}
                                  </button>
                                  <button
                                    onClick={resetMemberTransfer}
                                    disabled={memberTransfer.transferring}
                                    style={{
                                      padding: '4px 12px',
                                      border: '1px solid #dee2e6',
                                      borderRadius: '4px',
                                      backgroundColor: 'white',
                                      color: '#6c757d',
                                      cursor: 'pointer',
                                      fontSize: '0.75rem'
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </div>

                {/* Form Footer */}
                <div style={{
                  padding: '1rem 1.5rem',
                  borderTop: '1px solid #dee2e6',
                  display: 'flex',
                  justifyContent: 'space-between',
                  backgroundColor: '#f8f9fa'
                }}>
                  <div>
                    {editingCrew && (
                      <button
                        onClick={() => handleDeleteCrew(editingCrew.id)}
                        style={{
                          padding: '10px 20px',
                          border: 'none',
                          borderRadius: '4px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          cursor: 'pointer',
                          fontSize: '0.9rem'
                        }}
                      >
                        Delete Crew
                      </button>
                    )}
                  </div>
                  {formErrors.projects && (
                    <div style={{
                      padding: '0.5rem 0.75rem', marginBottom: '0.75rem',
                      backgroundColor: '#fee2e2', color: '#991b1b',
                      borderRadius: '4px', fontSize: '0.82rem',
                      border: '1px solid #fecaca'
                    }}>
                      {formErrors.projects}
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={resetForm}
                      style={{
                        padding: '10px 20px',
                        border: '1px solid #dee2e6',
                        borderRadius: '4px',
                        backgroundColor: 'white',
                        color: '#333',
                        cursor: 'pointer',
                        fontSize: '0.9rem'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveCrew}
                      disabled={saving}
                      style={{
                        padding: '10px 24px',
                        border: 'none',
                        borderRadius: '4px',
                        backgroundColor: saving ? '#6c757d' : '#007bff',
                        color: 'white',
                        cursor: saving ? 'not-allowed' : 'pointer',
                        fontSize: '0.9rem',
                        fontWeight: '600'
                      }}
                    >
                      {saving ? 'Saving...' : (editingCrew ? 'Update Crew' : 'Create Crew')}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      {showHelp && <TimeTrackerHelpModal onClose={() => setShowHelp(false)} />}
    </div>
  );
};

export default CrewManagementModal;
